/**
 * Bajar una planilla.
 *
 * **Se arma en el navegador con los datos que la pantalla ya tiene.** No hay una ruta que devuelva
 * CSV, y es por una razón concreta: la sesión viaja en un encabezado `Authorization`, y un enlace
 * de descarga no lleva encabezados. Servirlo desde el servidor obligaría a inventar una manera de
 * autenticar una descarga —un token en la dirección, que queda en el historial y en los registros
 * del servidor— para no ganar nada: los datos ya están acá.
 *
 * El precio es que la planilla sale de lo que se está mirando. Es también la garantía: **lo que se
 * baja es exactamente lo que está en la pantalla**, y no una segunda consulta que podría contestar
 * otra cosa.
 */

/**
 * Una celda, escapada como manda el CSV.
 *
 * Las comillas se duplican y la celda va entre comillas si tiene coma, comilla o salto de línea.
 * Sin esto, una razón social como "Pinturas del Litoral, S.A." parte la fila en dos columnas y la
 * planilla queda corrida desde ahí hasta el final.
 */
function celda(valor: string | number): string {
  const texto = String(valor);
  return /[",\n\r]/.test(texto) ? `"${texto.replaceAll('"', '""')}"` : texto;
}

/**
 * Arma el CSV y dispara la descarga.
 *
 * Va con **punto y coma** y con BOM. Los dos por lo mismo: un Excel en español abre un CSV con
 * coma metiendo todo en una columna, y sin BOM muestra "Recepción" como "RecepciÃ³n". Son dos
 * detalles feos que deciden si el contador puede usar el archivo o lo devuelve.
 */
export function bajarPlanilla(
  nombre: string,
  columnas: readonly string[],
  filas: readonly (readonly (string | number)[])[],
): void {
  const contenido = [columnas, ...filas].map((fila) => fila.map(celda).join(';')).join('\r\n');

  const enlace = document.createElement('a');
  enlace.href = URL.createObjectURL(
    new Blob([`\uFEFF${contenido}`], { type: 'text/csv;charset=utf-8' }),
  );
  enlace.download = `${nombre}.csv`;
  enlace.click();
  URL.revokeObjectURL(enlace.href);
}

/**
 * Baja un archivo de texto en **ISO 8859-1**, que es lo que el Libro de IVA Digital exige.
 *
 * > Los archivos deben ser generados con formato ANSI, para poder ser importados correctamente y
 * > los juegos de caracteres compatibles son ISO 8859-1 y Windows-1252.
 *
 * Es lo más fácil de pasar por alto y lo que rompe la importación entera. Un archivo de ancho fijo
 * con una "ñ" en UTF-8 lleva dos bytes donde el diseño espera uno, y **desde ese renglón hasta el
 * final todas las posiciones quedan corridas**. El organismo lo rechaza sin decir dónde.
 *
 * La conversión es byte a byte porque el `TextEncoder` del navegador **sólo sabe UTF-8**: no hay
 * manera de pedirle latin-1. Lo que no entra en un byte se reemplaza por un signo de pregunta, que
 * es lo que hace cualquier conversor y lo que mantiene el largo del renglón intacto.
 */
export function bajarEnLatin1(nombre: string, contenido: string): void {
  const bytes = new Uint8Array(contenido.length);
  for (let posicion = 0; posicion < contenido.length; posicion += 1) {
    const codigo = contenido.charCodeAt(posicion);
    bytes[posicion] = codigo < 256 ? codigo : 0x3f;
  }

  const enlace = document.createElement('a');
  enlace.href = URL.createObjectURL(new Blob([bytes], { type: 'text/plain' }));
  enlace.download = nombre;
  enlace.click();
  URL.revokeObjectURL(enlace.href);
}
